// pages/LoanRules/LoanRules.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  loanLimitRulesAdd,
  loanLimitRulesEdit,
  commonRunQuery,
  addRuleCondition,
  updateRuleCondition,
  addRuleAction,
  updateRuleAction,
} from "../../util/API/loans";
import CustomAlert from "../../components/UI/CustomAlert/CustomAlert";

function LoanRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productList, setProductList] = useState([]);
  const [sourceList, setSourceList] = useState({});

  // Main screen tabs (0 = Rules, 1 = Logs)
  const [mainTabValue, setMainTabValue] = useState(0);

  // Logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Rule form
  const [ruleForm, setRuleForm] = useState({
    name: "",
    is_active: true,
    priority: 100,
    match_logic: "ALL",
    min_factors: "",
    severity: "INFO",
    valid_from: "",
    valid_to: "",
  });

  // Conditions
  const [conditions, setConditions] = useState([]);
  const [condLoading, setCondLoading] = useState(false);
  const [openCondDialog, setOpenCondDialog] = useState(false);
  const [editingCond, setEditingCond] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [condForm, setCondForm] = useState({
    field: "score",
    operator: "=",
    value_low: "",
    value_high: "",
    window_days: "",
    weight: 1,
    source_id: "0",
  });

  // Actions
  const [actions, setActions] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [actionForm, setActionForm] = useState({
    target_scope: "GLOBAL",
    loanproduct_id: "",
    action_type: "SET_LIMIT_ABS",
    param_value: "",
    param_percent: "",
    param_delta: "",
  });

  // Alert
  const [alert, setAlert] = useState({
    showAlert: false,
    severity: "success",
    message: "",
    header: "",
  });

  // Helper to unescape HTML entities
  const unescapeHtml = (str) => {
    if (!str) return str;
    return str
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'");
  };

  // Fetch main rules
  useEffect(() => {
    fetchLoanRules();
  }, []);

  const fetchLoanRules = async () => {
    try {
      setLoading(true);
      const response = await commonRunQuery(340, "");

      if (response?.data?.header?.callStatus === "1" && response?.data?.header?.reccount !== "0") {
        const rawRules = Array.isArray(response?.data?.body?.record)
          ? response?.data?.body?.record
          : [response?.data?.body?.record];

        const formatted = rawRules.map((item) => ({
          id: item?.loanlimitrules?.id,
          name: item?.loanlimitrules?.name || "-",
          is_active: 
            item?.loanlimitrules?.is_active === "true" || 
            item?.loanlimitrules?.is_active === true || 
            item?.loanlimitrules?.is_active === 1 || 
            item?.loanlimitrules?.is_active === "1",
          priority: item?.loanlimitrules?.priority || 100,
          match_logic: item?.loanlimitrules?.match_logic || "ALL",
          min_factors: item?.loanlimitrules?.min_factors || null,
          severity: item?.loanlimitrules?.severity || "INFO",
          valid_from: item?.loanlimitrules?.valid_from || null,
          valid_to: item?.loanlimitrules?.valid_to || null,
          version: item?.loanlimitrules?.version || 1,
        }));

        setRules(formatted);
      } else {
        setRules([]);
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
      setAlert({ showAlert: true, severity: "error", header: "Error", message: "Failed to load rules." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (openActionDialog) {
      fetchProductList();
    }
  }, [openActionDialog]);

  const fetchProductList = async () => {
    try {
      setProductsLoading(true);
      const response = await commonRunQuery(268, "");

      if (response?.data?.header?.callStatus === "1" && response?.data?.header?.reccount !== "0") {
        let rawRecords = response?.data?.body?.record;

        if (!rawRecords) {
          rawRecords = [];
        } else if (!Array.isArray(rawRecords)) {
          rawRecords = [rawRecords];
        }

        const productsOnly = rawRecords.filter((item) => item?.lp);

        const formatted = productsOnly.map((item) => ({
          productid: item.lp.productid || "",
          name: item.lp.name || "Unknown Product",
        }));

        setProductList(formatted);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error fetching product list:", error);
      setProductList([]);
    } finally {
      setProductsLoading(false);
    }
  };

  // Fetch logs (global)
  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await commonRunQuery(343, "");

      if (response?.data?.header?.callStatus === "1" && response?.data?.header?.reccount !== "0") {
        const raw = Array.isArray(response?.data?.body?.record)
          ? response?.data?.body?.record
          : [response?.data?.body?.record];
        setLogs(raw.map(item => item.loanrulematchlogs || {}));
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (mainTabValue === 1) {
      fetchLogs();
    }
  }, [mainTabValue]);

  const loadConditionsAndActions = async (ruleId) => {
    try {
      setCondLoading(true);
      setActionLoading(true);

      const [condRes, actionRes, scoresRes] = await Promise.all([
        commonRunQuery(341, ruleId),
        commonRunQuery(342, ruleId),
        commonRunQuery(336, ruleId),
      ]);
      console.log("loadConditionsAndActions============",condRes?.data?.body?.record);
      console.log("loadConditionsAndActions_scoresRes============",scoresRes?.data?.body?.record);

      let scoreMap = {};

      if (scoresRes?.data?.header?.callStatus === "1" && scoresRes?.data?.header?.reccount !== "0") {
        const raw = Array.isArray(condRes?.data?.body?.record)
          ? condRes?.data?.body?.record
          : [condRes?.data?.body?.record];

        // Create a map from scoresRes: { "1": "PAIY", "2": "Kuunda", ... }
        scoreMap = (scoresRes?.data?.body?.record || []).reduce((acc, item) => {
          const { sourceid, sourcename } = item.userscoresources || {};
          if (sourceid && sourcename) acc[sourceid] = sourcename;
          return acc;
        }, {});
      }
      setSourceList(scoreMap);
      console.log("loadConditionsAndActions_scoreMap============",scoreMap);

      if (condRes?.data?.header?.callStatus === "1" && condRes?.data?.header?.reccount !== "0") {
        const raw = Array.isArray(condRes?.data?.body?.record)
          ? condRes?.data?.body?.record
          : [condRes?.data?.body?.record];

        const mappedConditions = raw.map((c) => {
          // Get the loanlimitruleconditions object
          const condition = c.loanlimitruleconditions || {};
      
          // Replace numeric source_id with the corresponding sourcename
          if (condition.source_id && scoreMap[condition.source_id]) {
            condition.source_name = scoreMap[condition.source_id];
          }
      
          return condition;
        });
        console.log("mappedConditions========",mappedConditions);
        setConditions(mappedConditions);
        // setConditions(raw.map((c) => c.loanlimitruleconditions || {}));
      } else {
        setConditions([]);
      }

      if (actionRes?.data?.header?.callStatus === "1" && actionRes?.data?.header?.reccount !== "0") {
        const raw = Array.isArray(actionRes?.data?.body?.record)
          ? actionRes?.data?.body?.record
          : [actionRes?.data?.body?.record];
        setActions(raw.map((a) => a.loanlimitruleactions || {}));
      } else {
        setActions([]);
      }
    } catch (error) {
      console.error("Error loading conditions/actions:", error);
      setAlert({ showAlert: true, severity: "error", header: "Error", message: "Failed to load details." });
    } finally {
      setCondLoading(false);
      setActionLoading(false);
    }
  };

  const handleOpenDialog = async (rule = null) => {
    if (rule) {
      setIsEditMode(true);
      setSelectedRule(rule);
      setRuleForm({
        name: rule.name,
        is_active: rule.is_active,
        priority: rule.priority,
        match_logic: rule.match_logic,
        min_factors: rule.min_factors !== null ? rule.min_factors : "",
        severity: rule.severity,
        valid_from: rule.valid_from && rule.valid_from !== "0000-00-00 00:00:00" 
          ? rule.valid_from.split(" ")[0] 
          : "",
        valid_to: rule.valid_to && rule.valid_to !== "0000-00-00 00:00:00" 
          ? rule.valid_to.split(" ")[0] 
          : "",
      });
      await loadConditionsAndActions(rule.id);
    } else {
      setIsEditMode(false);
      setSelectedRule(null);
      setRuleForm({
        name: "",
        is_active: true,
        priority: 100,
        match_logic: "ALL",
        min_factors: "",
        severity: "INFO",
        valid_from: "",
        valid_to: "",
      });
      setConditions([]);
      setActions([]);
    }
    setOpenDialog(true);
    setTabValue(0);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRule(null);
    setIsEditMode(false);
    setConditions([]);
    setActions([]);
  };

  const handleSaveRule = async () => {
    if (!ruleForm.name.trim()) {
      setAlert({ showAlert: true, severity: "warning", header: "Error", message: "Rule name is required." });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        location: "",
        name: ruleForm.name.trim(),
        is_active: ruleForm.is_active ? 1 : 0,
        priority: parseInt(ruleForm.priority) || 100,
        match_logic: ruleForm.match_logic,
        min_factors:
          ruleForm.match_logic === "MIN_FACTORS" && ruleForm.min_factors.trim() !== ""
            ? parseInt(ruleForm.min_factors)
            : null,
        severity: ruleForm.severity,
        valid_from: ruleForm.valid_from || null,
        valid_to: ruleForm.valid_to || null,
        version: isEditMode ? selectedRule.version : 1,
      };

      if (isEditMode) payload.id = selectedRule.id;

      const response = isEditMode ? await loanLimitRulesEdit(payload) : await loanLimitRulesAdd(payload);

      const isSuccess =
        response?.Status === "1" ||
        response?.data?.header?.callStatus === "1" ||
        response?.payload?.header?.callStatus === "1";

      if (isSuccess) {
        setAlert({ showAlert: true, severity: "success", header: "Success", message: `Rule ${isEditMode ? "updated" : "created"} successfully!` });
        handleCloseDialog();
        fetchLoanRules();
      } else {
        const msg = response?.payload?.msg || response?.data?.payload?.msg || "Operation failed.";
        setAlert({ showAlert: true, severity: "error", header: "Error", message: msg });
      }
    } catch (error) {
      console.error(error);
      setAlert({ showAlert: true, severity: "error", header: "Error", message: "Failed to save rule." });
    } finally {
      setSaving(false);
    }
  };

  // Conditions CRUD
  const handleOpenCondDialog = (cond = null) => {
    if (cond) {
      setEditingCond(cond);
      console.log("setEditingCond-----------",cond);
      setCondForm({
        field: cond.field,
        operator: unescapeHtml(cond.operator),
        value_low: cond.value_low || "",
        value_high: cond.value_high || "",
        window_days: cond.window_days || "",
        weight: cond.weight || 1,
        source_id: cond.source_id || "0",
      });
    } else {
      setEditingCond(null);
      setCondForm({
        field: "score",
        operator: "=",
        value_low: "",
        value_high: "",
        window_days: "",
        weight: 1,
        source_id: "0",
      });
    }
    setOpenCondDialog(true);
  };

  const handleSaveCondition = async () => {
    // VALIDATION: source_id must be a valid integer
    const sourceIdStr = condForm.source_id.trim();
    if (sourceIdStr !== "" && !/^\d+$/.test(sourceIdStr)) {
      setAlert({ 
        showAlert: true, 
        severity: "error", 
        header: "Invalid Input", 
        message: "Source ID must be a valid integer (e.g. 123)" 
      });
      return;
    }

    const payload = {
      location: "",
      rule_id: selectedRule.id,
      source_id: sourceIdStr === "" ? "0" : sourceIdStr,
      field: condForm.field,
      operator: condForm.operator,
      value_low: condForm.value_low ? parseFloat(condForm.value_low) : null,
      value_high: condForm.operator === "BETWEEN" 
        ? (condForm.value_high ? parseFloat(condForm.value_high) : null)
        : 0,
      window_days: condForm.window_days ? parseInt(condForm.window_days) : null,
      weight: parseInt(condForm.weight) || 1,
    };

    if (editingCond) payload.id = editingCond.id;

    try {
      const response = editingCond ? await updateRuleCondition(payload) : await addRuleCondition(payload);

      const isSuccess = 
        response?.callStatus === "1" || 
        response?.Status === "1" ||
        response?.data?.header?.callStatus === "1" ||
        response?.payload?.header?.callStatus === "1";

      if (isSuccess) {
        setAlert({ 
          showAlert: true, 
          severity: "success", 
          header: "Success", 
          message: "Condition saved successfully!" 
        });
        setOpenCondDialog(false);
        loadConditionsAndActions(selectedRule.id);
      } else {
        const msg = response?.payload?.msg || response?.data?.payload?.msg || response?.msg || "Operation failed.";
        setAlert({ 
          showAlert: true, 
          severity: "error", 
          header: "Error", 
          message: msg 
        });
      }
    } catch (error) {
      console.error("Error saving condition:", error);
      setAlert({ 
        showAlert: true, 
        severity: "error", 
        header: "Error", 
        message: "Error saving condition." 
      });
    }
  };

  // Actions CRUD
  const handleOpenActionDialog = (action = null) => {
    if (action) {
      setEditingAction(action);
      let params = {};
      try {
        if (action.params && typeof action.params === "string" && action.params.trim() !== "") {
          params = JSON.parse(action.params);
        }
      } catch (e) {
        console.warn("Invalid JSON in params:", action.params);
        params = {};
      }
      setActionForm({
        target_scope: action.target_scope,
        loanproduct_id: action.loanproduct_id || "",
        action_type: action.action_type,
        param_value: action.param_value || "",
        param_percent: action.param_percent || "",
        param_delta: params.delta || "",
      });
    } else {
      setEditingAction(null);
      setActionForm({
        target_scope: "GLOBAL",
        loanproduct_id: "",
        action_type: "SET_LIMIT_ABS",
        param_value: "",
        param_percent: "",
        param_delta: "",
      });
    }
    setOpenActionDialog(true);
  };

  const handleSaveAction = async () => {
    const paramsObj = {};

    if (["SET_LIMIT_ABS", "INCREASE_LIMIT_BY_ABS"].includes(actionForm.action_type)) {
      if (actionForm.param_value.trim() !== "") {
        paramsObj.value = actionForm.param_value.trim();
      }
    } else if (["INCREASE_LIMIT_BY_PCT", "DECREASE_LIMIT_BY_PCT"].includes(actionForm.action_type)) {
      if (actionForm.param_percent.trim() !== "") {
        paramsObj.percent = actionForm.param_percent.trim();
      }
    }

    const payload = {
      location: "",
      rule_id: selectedRule.id,
      target_scope: actionForm.target_scope,
      action_type: actionForm.action_type,
      loanproduct_id: actionForm.target_scope === "PRODUCT" && actionForm.loanproduct_id.trim() !== ""
        ? actionForm.loanproduct_id.trim()
        : 0,
      params: JSON.stringify(paramsObj),
    };

    if (editingAction) payload.id = editingAction.id;

    try {
      const response = editingAction ? await updateRuleAction(payload) : await addRuleAction(payload);

      const isSuccess =
        response?.callStatus === "1" ||
        response?.Status === "1" ||
        response?.data?.header?.callStatus === "1" ||
        response?.payload?.header?.callStatus === "1";

      if (isSuccess) {
        setAlert({ showAlert: true, severity: "success", header: "Success", message: "Action saved!" });
        setOpenActionDialog(false);
        loadConditionsAndActions(selectedRule.id);
      } else {
        const msg = response?.payload?.msg || response?.data?.payload?.msg || response?.msg || "Operation failed.";
        setAlert({ showAlert: true, severity: "error", header: "Error", message: msg });
      }
    } catch (error) {
      console.error("Error saving action:", error);
      setAlert({ showAlert: true, severity: "error", header: "Error", message: "Error saving action." });
    }
  };

  return (
    <Box sx={{ pt: 16, minWidth: "80%", mx: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, maxWidth: "1500px" }}>
        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: "1.75rem" }}>
          Loan Limit Rules
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add New Rule
        </Button>
      </Box>

      {/* Main Tabs */}
      <Tabs value={mainTabValue} onChange={(_, v) => setMainTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Rules" />
        <Tab label="Logs" />
      </Tabs>

      {/* Rules Tab */}
      {mainTabValue === 0 && (
        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, mb: 8 }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <CircularProgress />
              <Typography sx={{ mt: 2, fontSize: "1.1rem" }}>Loading rules...</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Rule ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Match Logic</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Min Factors</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Severity</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Valid From</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Valid To</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1.1rem", py: 4 }}>
                        No loan limit rules configured yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => (
                    <TableRow key={rule.id} hover>
                      <TableCell sx={{ fontSize: "1rem" }}>{rule.id}</TableCell>
                      <TableCell sx={{ fontSize: "1rem" }}>{rule.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={rule.is_active ? "Active" : "Inactive"}
                          color={rule.is_active ? "success" : "default"}
                          size="small"
                          sx={{ fontSize: "0.9rem" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem" }}>{rule.priority}</TableCell>
                      <TableCell>
                        <Chip label={rule.match_logic} size="small" sx={{ fontSize: "0.9rem" }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem" }}>{rule.min_factors || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          label={rule.severity}
                          color={
                            rule.severity === "BLOCK" ? "error" :
                            rule.severity === "LIMIT_REDUCE" ? "warning" : "info"
                          }
                          size="small"
                          sx={{ fontSize: "0.9rem" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem" }}>{rule.valid_from || "-"}</TableCell>
                      <TableCell sx={{ fontSize: "1rem" }}>{rule.valid_to || "-"}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Rule">
                          <IconButton onClick={() => handleOpenDialog(rule)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      {/* Logs Tab */}
      {mainTabValue === 1 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Loan Limit Rules Logs
          </Typography>
          {logsLoading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress />
              <Typography>Loading logs...</Typography>
            </Box>
          ) : logs.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No logs available.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date/Time</strong></TableCell>
                    <TableCell><strong>Rule ID</strong></TableCell>
                    <TableCell><strong>User ID</strong></TableCell>
                    <TableCell><strong>Factors Matched</strong></TableCell>
                    <TableCell><strong>Action Summary</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => {
                    const date = new Date(log.matched).toLocaleString();

                    return (
                      <TableRow key={log.id}>
                        <TableCell>{date}</TableCell>
                        <TableCell>{log.rule_id}</TableCell>
                        <TableCell>{log.user_id}</TableCell>
                        <TableCell>{log.factors_matched} / {log.total_factors}</TableCell>
                        <TableCell>
                          <pre style={{ 
                            margin: 0, 
                            whiteSpace: "pre-wrap", 
                            wordBreak: "break-word", 
                            fontSize: "0.85rem" 
                          }}>
                            {unescapeHtml(log.action_summary) || "-"}
                          </pre>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontSize: "1.6rem", fontWeight: "bold" }}>
          {isEditMode ? ` Rule ID: ${selectedRule?.id}` : "Create"} 
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
            <Tab label="Rule Details" />
            <Tab label="Conditions" disabled={!selectedRule} />
            <Tab label="Actions" disabled={!selectedRule} />
          </Tabs>

          {/* Tab 0: Rule Form */}
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField label="Rule Name" value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} fullWidth required />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Priority" type="number" value={ruleForm.priority} onChange={(e) => setRuleForm({ ...ruleForm, priority: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Match Logic" value={ruleForm.match_logic} onChange={(e) => setRuleForm({ ...ruleForm, match_logic: e.target.value })} fullWidth>
                  <MenuItem value="ALL">ALL Factors</MenuItem>
                  <MenuItem value="ANY">ANY Factors</MenuItem>
                  <MenuItem value="MIN_FACTORS">Minimum Factors</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Min Factors"
                  type="number"
                  value={ruleForm.min_factors}
                  onChange={(e) => setRuleForm({ ...ruleForm, min_factors: e.target.value })}
                  fullWidth
                  disabled={ruleForm.match_logic !== "MIN_FACTORS"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Severity" value={ruleForm.severity} onChange={(e) => setRuleForm({ ...ruleForm, severity: e.target.value })} fullWidth>
                  <MenuItem value="INFO">Informational</MenuItem>
                  <MenuItem value="LIMIT_REDUCE">Limit Reduction</MenuItem>
                  <MenuItem value="BLOCK">Block</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Valid From" type="date" value={ruleForm.valid_from} onChange={(e) => setRuleForm({ ...ruleForm, valid_from: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Valid To" type="date" value={ruleForm.valid_to} onChange={(e) => setRuleForm({ ...ruleForm, valid_to: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={ruleForm.is_active} onChange={(e) => setRuleForm({ ...ruleForm, is_active: e.target.checked })} />}
                  label="Active"
                />
              </Grid>
            </Grid>
          )}

          {/* Tab 1: Conditions */}
          {tabValue === 1 && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenCondDialog()}>
                  Add Condition
                </Button>
              </Box>
              {condLoading ? (
                <Box textAlign="center" py={4}><CircularProgress /></Box>
              ) : conditions.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={4}>No conditions defined</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>ID</strong></TableCell>
                        <TableCell><strong>Source ID</strong></TableCell>
                        <TableCell><strong>Field</strong></TableCell>
                        <TableCell><strong>Operator</strong></TableCell>
                        <TableCell><strong>Value</strong></TableCell>
                        <TableCell><strong>Window Days</strong></TableCell>
                        <TableCell><strong>Weight</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {conditions.map((cond) => (
                        <TableRow key={cond.id}>
                          <TableCell>{cond.id}</TableCell>
                          <TableCell>{cond.source_name || "N/A"}</TableCell>
                          <TableCell>{cond.field.charAt(0).toUpperCase() + cond.field.slice(1)}</TableCell>
                          <TableCell>{unescapeHtml(cond.operator)}</TableCell>
                          <TableCell>
                            {cond.operator === "BETWEEN"
                              ? `${cond.value_low} - ${cond.value_high}`
                              : cond.value_low}
                          </TableCell>
                          <TableCell>{cond.window_days || "-"}</TableCell>
                          <TableCell>{cond.weight}</TableCell>
                          <TableCell align="center">
                            <IconButton onClick={() => handleOpenCondDialog(cond)}><EditIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Tab 2: Actions */}
          {tabValue === 2 && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenActionDialog()}>
                  Add Action
                </Button>
              </Box>
              {actionLoading ? (
                <Box textAlign="center" py={4}><CircularProgress /></Box>
              ) : actions.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={4}>No actions defined</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>ID</strong></TableCell>
                        <TableCell><strong>Scope</strong></TableCell>
                        <TableCell><strong>Product ID</strong></TableCell>
                        <TableCell><strong>Action Type</strong></TableCell>
                        <TableCell><strong>Parameter</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {actions.map((act) => {
                        let params = {};
                        try {
                          if (act.params && typeof act.params === "string" && act.params.trim() !== "") {
                            params = JSON.parse(act.params);
                          }
                        } catch (e) {
                          console.warn("Invalid JSON in params:", act.params);
                          params = {};
                        }

                        const paramDisplay =
                          params.value ? `Value: ${params.value}` :
                          params.percent ? `Percent: ${params.percent}%` :
                          params.delta ? `Delta: ${params.delta}` : "-";

                        return (
                          <TableRow key={act.id}>
                            <TableCell>{act.id}</TableCell>
                            <TableCell>{act.target_scope}</TableCell>
                            <TableCell>{act.loanproduct_id || "-"}</TableCell>
                            <TableCell>{act.action_type.replace(/_/g, " ")}</TableCell>
                            <TableCell>{act.param_percent || act.param_value}</TableCell>
                            <TableCell align="center">
                              <IconButton onClick={() => handleOpenActionDialog(act)}><EditIcon fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {tabValue === 0 && (
            <>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveRule} disabled={saving}>
                {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : (isEditMode ? "Update" : "Create")} Rule
              </Button>
            </>
          )}
          {tabValue > 0 && <Button onClick={() => setTabValue(0)}>Back to Rule</Button>}
        </DialogActions>
      </Dialog>

      {/* Condition Dialog */}
      <Dialog open={openCondDialog} onClose={() => setOpenCondDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCond ? "Edit" : "Add"} Condition</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* <Grid item xs={12}>
              <TextField 
                label="Source ID" 
                value={condForm.source_id} 
                onChange={(e) => setCondForm({ ...condForm, source_id: e.target.value })} 
                fullWidth 
                helperText="Enter source ID (e.g. credit bureau ID)"
              />
            </Grid> */}
            <Grid item xs={12}>
              <TextField select label="Source ID" value={condForm.source_id} onChange={(e) => setCondForm({ ...condForm, source_id: e.target.value }) } fullWidth helperText="Select source" >
                {Object.entries(sourceList).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Field" value={condForm.field} onChange={(e) => setCondForm({ ...condForm, field: e.target.value })} fullWidth>
                <MenuItem value="score">Score</MenuItem>
                <MenuItem value="delta_days">Delta Days</MenuItem>
                <MenuItem value="age_days">Age Days</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Operator" value={condForm.operator} onChange={(e) => setCondForm({ ...condForm, operator: e.target.value })} fullWidth>
                <MenuItem value="<">&lt;</MenuItem>
                <MenuItem value="<=">≤</MenuItem>
                <MenuItem value=">">&gt;</MenuItem>
                <MenuItem value=">=">≥</MenuItem>
                <MenuItem value="=">=</MenuItem>
                <MenuItem value="!=">≠</MenuItem>
                <MenuItem value="BETWEEN">BETWEEN</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Value Low" type="number" value={condForm.value_low} onChange={(e) => setCondForm({ ...condForm, value_low: e.target.value })} fullWidth />
            </Grid>
            {condForm.operator === "BETWEEN" && (
              <Grid item xs={12}>
                <TextField label="Value High" type="number" value={condForm.value_high} onChange={(e) => setCondForm({ ...condForm, value_high: e.target.value })} fullWidth />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField label="Window Days" type="number" value={condForm.window_days} onChange={(e) => setCondForm({ ...condForm, window_days: e.target.value })} fullWidth helperText="Optional, for delta/age fields" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Weight" type="number" value={condForm.weight} onChange={(e) => setCondForm({ ...condForm, weight: e.target.value })} fullWidth />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCondDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCondition}>Save Condition</Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={openActionDialog} onClose={() => setOpenActionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAction ? "Edit" : "Add"} Action</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField select label="Target Scope" value={actionForm.target_scope} onChange={(e) => setActionForm({ ...actionForm, target_scope: e.target.value, loanproduct_id: "" })} fullWidth>
                <MenuItem value="GLOBAL">Global</MenuItem>
                <MenuItem value="PRODUCT">Product</MenuItem>
              </TextField>
            </Grid>
            {actionForm.target_scope === "PRODUCT" && (
              <Grid item xs={12}>
                <TextField
                  select
                  label="Loan Product ID"
                  value={actionForm.loanproduct_id}
                  onChange={(e) => setActionForm({ ...actionForm, loanproduct_id: e.target.value })}
                  fullWidth
                  disabled={productsLoading}
                >
                  {productsLoading ? (
                    <MenuItem disabled>Loading products...</MenuItem>
                  ) : productList.length === 0 ? (
                    <MenuItem disabled>No products available</MenuItem>
                  ) : (
                    productList.map((product) => (
                      <MenuItem key={product.productid} value={product.productid}>
                        {product.productid} - {product.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField select label="Action Type" value={actionForm.action_type} onChange={(e) => setActionForm({ ...actionForm, action_type: e.target.value, param_value: "", param_percent: "" })} fullWidth>
                <MenuItem value="SET_LIMIT_ABS">Set Limit (Absolute)</MenuItem>
                <MenuItem value="SET_LIMIT_MAX">Set Limit (Max)</MenuItem>
                <MenuItem value="INCREASE_LIMIT_BY_PCT">Increase by %</MenuItem>
                <MenuItem value="DECREASE_LIMIT_BY_PCT">Decrease by %</MenuItem>
                <MenuItem value="INCREASE_LIMIT_BY_ABS">Increase by Amount</MenuItem>
              </TextField>
            </Grid>
            {["SET_LIMIT_ABS", "INCREASE_LIMIT_BY_ABS"].includes(actionForm.action_type) && (
              <Grid item xs={12}>
                <TextField label="Value" type="number" value={actionForm.param_value} onChange={(e) => setActionForm({ ...actionForm, param_value: e.target.value })} fullWidth />
              </Grid>
            )}
            {["INCREASE_LIMIT_BY_PCT", "DECREASE_LIMIT_BY_PCT"].includes(actionForm.action_type) && (
              <Grid item xs={12}>
                <TextField label="Percent" type="number" value={actionForm.param_percent} onChange={(e) => setActionForm({ ...actionForm, param_percent: e.target.value })} fullWidth helperText="e.g. 10 for 10%" />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenActionDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAction}>Save Action</Button>
        </DialogActions>
      </Dialog>

      <CustomAlert
        severity={alert.severity}
        show={alert.showAlert}
        header={alert.header}
        message={alert.message}
        onClick={() => setAlert({ ...alert, showAlert: false })}
        autoclose={alert.severity !== "error"}
      />
    </Box>
  );
}

export default LoanRules;